/*
 *   File : reward.js
 *   Author : https://evoqins.com
 *   Description : Rewards detail
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

//Custom component
import { DataTableComponent } from '../../../../Component/DataTable';
import { SummaryCard } from '../../../../Component/Cards';
import { APIService } from '../../../../Service';
import { Loader } from '../../../../Component/Others';
import { EmptyScreen } from '../../../../Component/EmptyComponent';
import { TextButton } from '../../../../Component/Buttons';

const TABLE_CATEGORY_TAB = [
    {
        id: 1,
        name: "Resource points"
    },
    {
        id: 2,
        name: "Sales points"
    },
    {
        id: 3,
        name: "Other points"
    },
];

const ITEMS_PER_PAGE = 15;

const LEARNING_SORT_FIELDS = [
    'date',
    'earned_from',
    'earned_point',
];

const OCR_SORT_FIELDS = [
    'receipt_no',
    'date',
    'quantity',
    'points',
];

const OTHER_SORT_FIELDS = [
    'date',
    'reward',
    'earned_point',
];

const Reward = (props) => {

    const navigate = useNavigate();

    const [tabIndex, setTabIndex] = useState(1);

    const [rewardData, setRewardData] = useState({});
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [tableLoading, setTableLoading] = useState(true);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const allowedSortFields = tabIndex === 1
        ? LEARNING_SORT_FIELDS
        : tabIndex === 2
            ? OCR_SORT_FIELDS
            : OTHER_SORT_FIELDS;

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }
        _getRewardDetails();
    }, [currentPage, tabIndex]);

    // handle tab change 
    const _handleTabChange = (id) => {
        setTabIndex(id);
        setSortField(null);
        setSortDirection(null);
        setCurrentPage(1);
        setTableLoading(true);
    };

    // handle resource table page change 
    const _handleTablePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !allowedSortFields.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setTableLoading(true);

        // Only skip the list useEffect when page will actually change.
        // If already on page 1, useEffect won't run and a stuck skip flag
        // would block the next pagination API call (loader stuck).
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getRewardDetails(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    // resource data table 
    const TABLE_COLUMNS_RESOURCES = [
        {
            name: 'Date',
            id: 'date',
            sortField: 'date',
            selector: row => row.date,
            width: "140px",
            sortable: true
        },
        {
            name: 'Points earned from',
            id: 'earned_from',
            sortField: 'earned_from',
            selector: row => row.earned_from,
            width: "200px",
            sortable: true
        },
        {
            name: "Points",
            id: 'earned_point',
            sortField: 'earned_point',
            selector: row => row.earned_point,
            sortable: true
        }
    ];

    // Sales / OCR data table 
    const TABLE_COLUMNS_SALES = [
        {
            name: 'Receipt no',
            id: 'receipt_no',
            sortField: 'receipt_no',
            selector: row => row.receipt_no || "-",
            cell: row => <>{row.receipt_no || "-"}</>,
            width: "140px",
            sortable: true
        },
        {
            name: 'Date',
            id: 'date',
            sortField: 'date',
            selector: row => row.date,
            width: "140px",
            sortable: true
        },
        {
            name: 'Quantity',
            id: 'quantity',
            sortField: 'quantity',
            selector: row => row.quantity ?? row.sales_per_unit ?? "-",
            width: "140px",
            sortable: true
        },
        // {
        //     name: 'Product',
        //     selector: row => row.product || "-",
        //     width: "220px",
        //     sortable: false
        // },
        {
            name: "Points",
            id: 'points',
            sortField: 'points',
            selector: row => row.points ?? row.earned_point ?? "-",
            width: "140px",
            sortable: true
        },
        {
            name: 'Action',
            cell: row =>
                <TextButton name="View"
                    className="e-font-weight-400 cursor-pointer"
                    icon={true}
                    onPress={() => {
                        if (!row.receipt_id) return;
                        navigate("/invoices/detail", {
                            state: {
                                id: row.receipt_id
                            }
                        });
                    }}
                />,
            width: "100px"
        }
    ];

    // other data table 
    const TABLE_COLUMNS_OTHER = [
        {
            name: 'Date',
            id: 'date',
            sortField: 'date',
            selector: row => row.date,
            width: "140px",
            sortable: true
        },
        {
            name: 'Reward',
            id: 'reward',
            sortField: 'reward',
            selector: row => row.reward,
            width: "160px",
            sortable: true
        },
        {
            name: "Points",
            id: 'earned_point',
            sortField: 'earned_point',
            selector: row => row.earned_point,
            sortable: true
        }

    ];

    // API - Get sales specialist reward data
    const _getRewardDetails = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        const effective_sort_by = sort_by && allowedSortFields.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        let is_post = true;
        let url = 'CA/sales-specialist/rewards';
        let data = {
            "customer_id": props.customerId,
            "filter": tabIndex,
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setRewardData(response.data);
            } else {
                setRewardData({ data: [] });
            }
            setLoading(false);
            setTableLoading(false);
        });
    };

    const dataTableProps = {
        pagination: true,
        paginationServer: true,
        paginationTotalRows: rewardData.total_count,
        paginationPerPage: ITEMS_PER_PAGE,
        paginationDefaultPage: currentPage,
        paginationResetDefaultPage: false,
        sortServer: true,
        defaultSortFieldId: sortField,
        defaultSortAsc: sortDirection !== 'desc',
        onSort: _handleSort,
        onChangePage: _handleTablePageChange,
    };

    return (
        <>
            {/* Summary  */}
            <div className='e-admin-box-shadow p-4 e-bg-white e-border-radius-16'>
                <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px'>
                    Point summary
                </h4>
                <div className='row g-3 row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 row-cols-1  padding-10px-top'>

                    {/* <div className='col'>
                        <SummaryCard title="Store level points"
                            loader={loading}
                            quantity={rewardData.store_level_reward} />
                    </div> */}
                    <div className='col'>
                        <SummaryCard title="Resource points"
                            loader={loading}
                            quantity={rewardData.resourse_reward} />
                    </div>
                    <div className='col'>
                        <SummaryCard title="Quiz points"
                            loader={loading}
                            quantity={rewardData.quiz_reward} />
                    </div>
                    <div className='col'>
                        <SummaryCard title="Points from videos"
                            loader={loading}
                            quantity={rewardData.video_reward} />
                    </div>
                    {/* <div className='col'>
                        <SummaryCard title="Flash quiz points"
                            loader={loading}
                            quantity={rewardData.flash_quiz_reward} />
                    </div> */}
                    <div className='col'>
                        <SummaryCard title="Sales points"
                            loader={loading}
                            quantity={rewardData.salse_reward} />
                    </div>
                    <div className='col'>
                        <SummaryCard title="Other points"
                            loader={loading}
                            quantity={rewardData.other_reward} />
                    </div>
                </div>
            </div>

            {/* Courses created table */}
            <div className='margin-20px-top e-bg-white e-admin-box-shadow py-4 e-border-radius-16 '>
                <h4 className='mb-3 px-4 e-text-rich-black e-alt-font-noto-sans e-font-18 e-font-weight-500 line-height-18px'>
                    Points details
                </h4>

                {/* table category tab */}
                <div className='d-flex gap-0-16 px-4 overflow-auto pb-xl-0 pb-lg-0 pb-md-0 pb-1 e-tab-scroll'>
                    {TABLE_CATEGORY_TAB.map((data, key) => (
                        <div key={key} className={`${tabIndex === data.id ? 'e-bg-lust e-text-white' : 'e-text-rich-black'} e-border-1px-cool-grey e-border-radius-24 cursor-pointer`}
                            onClick={() => _handleTabChange(data.id)} >
                            <p className='mb-0 px-4 padding-12px-tb e-alt-font-noto-sans  e-font-14 e-font-weight-500 line-height-14px w-max-content'>
                                {data.name}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Data table */}
                <div className='e-table'>
                    {/* resource reward point table */}
                    {tabIndex === 1 ?
                        <>
                            {tableLoading ?
                                // loader 
                                <Loader />
                                :
                                <>
                                    {rewardData?.data?.length > 0 ?
                                        <DataTableComponent
                                            key={`reward-learning-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                            columns={TABLE_COLUMNS_RESOURCES}
                                            data={rewardData.data || []}
                                            {...dataTableProps} />
                                        :
                                        // empty screen
                                        <div className='h-100vh pt-5 mt-5'>
                                            <EmptyScreen message="No data found" />
                                        </div>
                                    }
                                </>
                            }
                        </>
                        :
                        // sales reward point data table
                        tabIndex === 2 ?
                            <>
                                {tableLoading ?
                                    // loader 
                                    <Loader />
                                    :
                                    <>
                                        {rewardData?.data?.length > 0 ?
                                            <DataTableComponent
                                                key={`reward-ocr-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                                columns={TABLE_COLUMNS_SALES}
                                                data={rewardData.data || []}
                                                {...dataTableProps} />
                                            :
                                            // empty screen
                                            <div className='h-100vh pt-5 mt-5'>
                                                <EmptyScreen message="No data found" />
                                            </div>
                                        }
                                    </>
                                }
                            </>
                            :
                            // other reward point data table
                            <>
                                {tableLoading ?
                                    // loader 
                                    <Loader />
                                    :
                                    <>
                                        {rewardData?.data?.length > 0 ?
                                            <DataTableComponent
                                                key={`reward-other-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                                columns={TABLE_COLUMNS_OTHER}
                                                data={rewardData.data || []}
                                                {...dataTableProps} />
                                            :
                                            // empty screen
                                            <div className='h-100vh pt-5 mt-5'>
                                                <EmptyScreen message="No data found" />
                                            </div>
                                        }
                                    </>
                                }
                            </>
                    }
                </div>
            </div>
        </>
    )
}

export default Reward
